import type { ColumnDef } from '@tanstack/vue-table'
import { ref, computed, type ComputedRef } from 'vue'
import type { TableColumnSetting } from '@lib/components/CTableColumnsSettings/CTableColumnsSettings.vue'

export type TableSettings = {
  columns: {
    [key: string]: {
      visible: boolean
      order: number
    }
  }
}

export function useTableColumnsSettings(
  tableSettingsKey: string,
  columns: ColumnDef<Record<string, any>, any>[],
  excludeColumnIds: string[] = ['actions'],
) {
  function generateDefaultTableSettings() {
    return columns
      .filter((column) => column.id && !excludeColumnIds.includes(column.id)) // Ensure `id` is defined
      .reduce(
        (settings, column, index) => {
          if (column.id) {
            settings.columns[column.id] = { visible: true, order: index + 1 }
          }
          return settings
        },
        { columns: {} as TableSettings['columns'] },
      )
  }

  const currentTableSettings = ref<TableSettings>(getTableSettings())

  function getTableSettings(): TableSettings {
    const tableSettingsJson = localStorage.getItem(tableSettingsKey)
    if (tableSettingsJson) {
      try {
        const parsedSettings = JSON.parse(tableSettingsJson) as Partial<TableSettings>

        return { columns: { ...generateDefaultTableSettings().columns, ...parsedSettings.columns } }
      } catch {
        console.error('Failed to parse table settings from localStorage. Using default settings.')
      }
    }

    return generateDefaultTableSettings()
  }

  function updateTableSettings(settings: TableSettings) {
    localStorage.setItem(tableSettingsKey, JSON.stringify(settings))
    currentTableSettings.value = { ...settings }
  }

  function onColumnsMove({ targetItemId, itemId }: { targetItemId: string; itemId: string }) {
    const columns = currentTableSettings.value.columns
    if (columns[itemId] && columns[targetItemId]) {
      const itemOrder = columns[itemId].order
      const targetItemOrder = columns[targetItemId].order

      const moveDirection = itemOrder > targetItemOrder ? 'up' : 'down'

      Object.entries(columns).forEach(([key, value]) => {
        if (moveDirection === 'up') {
          if (value.order >= targetItemOrder && value.order < itemOrder) {
            columns[key].order += 1
          }
        } else if (value.order <= targetItemOrder && value.order > itemOrder) {
          columns[key].order -= 1
        }
      })

      columns[itemId].order = moveDirection === 'up' ? targetItemOrder : targetItemOrder + 1
    }

    const sortedKeys = Object.keys(columns).sort((a, b) => columns[a].order - columns[b].order)
    sortedKeys.forEach((key, index) => {
      columns[key].order = index + 1
    })

    updateTableSettings(currentTableSettings.value)
  }

  function onColumnsSelect({ id, checked }: { id: string; checked: boolean }) {
    currentTableSettings.value.columns[id].visible = checked
    updateTableSettings(currentTableSettings.value)
  }

  const tableColumnsSettings: ComputedRef<TableColumnSetting[]> = computed(() => {
    return columns
      .filter((column) => column.id && !excludeColumnIds.includes(column.id)) // Ensure `id` is defined
      .map((column) => ({
        id: column.id as string, // Type cast `id` to `string` as we've already checked it's not `undefined`
        name: column.header as string, //Note: support function in the future if is needed
        isSelected: currentTableSettings.value.columns[column.id as string]?.visible ?? true, // Type cast `id` to `string`
      }))
      .sort((a, b) => {
        const orderA = currentTableSettings.value.columns[a.id]?.order ?? Number.MAX_VALUE
        const orderB = currentTableSettings.value.columns[b.id]?.order ?? Number.MAX_VALUE
        return orderA - orderB
      })
  })

  const columnsFromSettings: ComputedRef<ColumnDef<Record<string, any>, any>[]> = computed(() => {
    const selectedColumns = tableColumnsSettings.value
      .filter((item) => item.isSelected)
      .map((item) => columns.find((column) => column.id === item.id))
      .filter((column): column is ColumnDef<Record<string, any>, any> => column !== undefined)

    const actionColumn = columns.find((column) => column.id === excludeColumnIds[0])
    if (actionColumn) {
      selectedColumns.push(actionColumn)
    }

    return selectedColumns
  })

  function restoreColumns() {
    const newSettings = generateDefaultTableSettings()
    updateTableSettings(newSettings)
  }

  return {
    tableColumnsSettings,
    columnsFromSettings,
    onColumnsMove,
    onColumnsSelect,
    restoreColumns,
  }
}
